import React, { useState } from 'react'
import { TacticRefDoc } from '@models/index'
import './RefDocBadge.css'

interface RefDocBadgeProps {
  refDoc: TacticRefDoc
  compact?: boolean
}

/**
 * Strips the "doc_" prefix and "_id" suffix from Ref_Document node IDs
 * and converts underscores to spaces for readable display names.
 */
function formatRefDocName(name: string): string {
  return name
    .replace(/^doc_/, '')
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

export const RefDocBadge: React.FC<RefDocBadgeProps> = ({ refDoc, compact = false }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const displayName = formatRefDocName(refDoc.name)
  const hasDetails = !!(refDoc.description || refDoc.excerpt)

  return (
    <div className={`refdoc-badge ${compact ? 'compact' : ''} ${isExpanded ? 'expanded' : ''}`}>
      <div
        className="refdoc-badge-header"
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
        style={{ cursor: hasDetails ? 'pointer' : 'default' }}
      >
        <span className="refdoc-badge-icon" title="Reference Document">📄</span>
        <span className="refdoc-badge-name" title={displayName}>
          {displayName}
        </span>
        {hasDetails && (
          <span className="refdoc-badge-toggle">{isExpanded ? '▼' : '▶'}</span>
        )}
      </div>
      {isExpanded && hasDetails && (
        <div className="refdoc-badge-details">
          {refDoc.description && (
            <p className="refdoc-description">{refDoc.description}</p>
          )}
          {refDoc.excerpt && (
            <blockquote className="refdoc-excerpt">
              {refDoc.excerpt}
            </blockquote>
          )}
        </div>
      )}
    </div>
  )
}

interface RefDocBadgeListProps {
  refDocs: TacticRefDoc[]
  compact?: boolean
  maxDisplay?: number
  startExpanded?: boolean
}

export const RefDocBadgeList: React.FC<RefDocBadgeListProps> = ({
  refDocs,
  compact = false,
  maxDisplay = 5,
  startExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(startExpanded)

  if (!refDocs || refDocs.length === 0) return null

  // Collapsed: show compact summary badge
  if (!isExpanded) {
    const names = refDocs.slice(0, 3).map(r => formatRefDocName(r.name))
    const preview = names.join(', ') + (refDocs.length > 3 ? '...' : '')
    return (
      <div className="refdoc-badge-list">
        <button
          className="refdoc-summary-toggle"
          onClick={() => setIsExpanded(true)}
          title={`${refDocs.length} References: ${refDocs.map(r => formatRefDocName(r.name)).join(', ')}`}
        >
          <span className="refdoc-badge-icon">📄</span>
          <span className="refdoc-summary-count">
            {refDocs.length} Reference{refDocs.length !== 1 ? 's' : ''}
          </span>
          <span className="refdoc-summary-preview">{preview}</span>
          <span className="refdoc-summary-expand">▶</span>
        </button>
      </div>
    )
  }

  // Expanded: show full ref doc badges
  const displayed = refDocs.slice(0, maxDisplay)
  const hasMore = refDocs.length > maxDisplay

  return (
    <div className="refdoc-badge-list expanded">
      <button
        className="refdoc-summary-toggle active"
        onClick={() => setIsExpanded(false)}
      >
        <span className="refdoc-badge-icon">📄</span>
        <span className="refdoc-summary-count">
          {refDocs.length} Reference{refDocs.length !== 1 ? 's' : ''}
        </span>
        <span className="refdoc-summary-expand">▼</span>
      </button>
      {displayed.map((r) => (
        <RefDocBadge key={r.id} refDoc={r} compact={compact} />
      ))}
      {hasMore && (
        <span className="refdoc-badge-overflow">
          +{refDocs.length - maxDisplay} more
        </span>
      )}
    </div>
  )
}
